import React, { useState } from 'react';
import { Box, Button, Text, Modal } from '@rsos/base-components';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';

interface ResetSSOConfigurationProps {
  onReset: () => Promise<void>;
}

const ResetSSOConfiguration: React.FC<ResetSSOConfigurationProps> = ({
  onReset,
}) => {
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);

  const handleResetClick = (): void => {
    setShowConfirmation(true);
  };

  const handleConfirmReset = async (): Promise<void> => {
    if (isResetting) {
      return;
    }

    setIsResetting(true);
    try {
      await onReset();
      setShowConfirmation(false);
    } catch (error) {
      setShowConfirmation(false);
    } finally {
      setIsResetting(false);
    }
  };

  const handleCancel = (): void => {
    setShowConfirmation(false);
  };

  return (
    <>
      <Button
        btnType="warning_tertiary"
        onClick={handleResetClick}
        disabled={isResetting}
      >
        Reset SSO Configuration
      </Button>

      <Modal
        isOpened={showConfirmation}
        onClose={handleCancel}
        width={500}
        id="reset-sso-configuration-modal"
      >
        <ModalHeader onClose={handleCancel}>
          <Box width={500}>
            <Text size="xlarge" fontWeight="bold" color="primaryText">
              Confirm SSO Configuration Reset
            </Text>
          </Box>
        </ModalHeader>
        <ModalBody>
          <Text size="normal" color="primaryText" mb={15}>
            This will reset the SSO domain and all SSO-related settings back to
            default values. Use this when having issues setting up
            configuration, logging in due to conflicting configurations, when
            you need to change your domain, or when you need to start your SSO
            configuration from scratch.
          </Text>
          <Text size="normal" color="warningText" fontWeight="bold">
            Warning: This action will clear all current SSO settings and cannot
            be undone. The administrator will need to reconfigure SSO completely
            after this reset.
          </Text>
        </ModalBody>
        <ModalFooter mt={32}>
          <Box horizontal flow={12} width="100%">
            <Button
              btnType="secondary"
              onClick={handleCancel}
              disabled={isResetting}
              grow
              noMargin
            >
              Cancel
            </Button>
            <Button
              btnType="warning_primary"
              onClick={handleConfirmReset}
              disabled={isResetting}
              grow
              noMargin
            >
              {isResetting ? 'Resetting...' : 'Confirm Reset'}
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ResetSSOConfiguration;
